import { apiService as api } from '@/store/apiService';

export interface PendingApprovalListItem {
    id: string;
    documentId: string;
    batchId: string;
    stepId: string;
    targetGroupId?: string | null;
    targetUserId?: string | null;
    state: string;
    allowForward: boolean;
    message?: string | null;
    openedAt: string;
    deadlineAt?: string | null;
}

export interface ApprovalDecisionRow {
    id: string;
    actorUserId: string;
    actorEmail?: string | null;
    decision: 'Approve' | 'Deny' | 'Forward';
    reason?: string | null;
    forwardedToGroupId?: string | null;
    forwardedToUserId?: string | null;
    via: 'Dashboard' | 'EmailToken';
    createdAt: string;
}

export interface ApprovalDetail extends PendingApprovalListItem {
    targetGroupName?: string | null;
    notificationMode: string;
    finalDecision?: string | null;
    resolvedAt?: string | null;
    decisions: ApprovalDecisionRow[];
}

export interface PendingListResponse {
    items: PendingApprovalListItem[];
    total: number;
}

export interface CountResponse {
    count: number;
}

export interface ActionRequest {
    decision: 'Approve' | 'Deny' | 'Forward';
    reason?: string;
    forwardToGroupId?: string;
    forwardToUserId?: string;
}

export interface EmailLandingResponse {
    token: string;
    action: 'Approve' | 'Deny' | 'Forward';
    approvalId: string;
    documentId: string;
    message?: string | null;
    openedAt: string;
    deadlineAt?: string | null;
    allowForward: boolean;
}

export interface GroupOption {
    id: string;
    name: string;
    description?: string;
}

const addTagTypes = ['approvals_count', 'approvals_list', 'approval_detail'] as const;

const ApprovalsAPI = api
    .enhanceEndpoints({ addTagTypes })
    .injectEndpoints({
        endpoints: (build) => ({
            getPendingApprovalCount: build.query<CountResponse, void>({
                query: () => ({ url: '/api/approvals/count' }),
                providesTags: ['approvals_count']
            }),
            getPendingApprovals: build.query<PendingListResponse, { page?: number; pageSize?: number } | void>({
                query: (arg) => {
                    const page = (arg && (arg as any).page) ?? 0;
                    const pageSize = (arg && (arg as any).pageSize) ?? 50;
                    return { url: `/api/approvals/pending?page=${page}&pageSize=${pageSize}` };
                },
                providesTags: ['approvals_list']
            }),
            getApproval: build.query<ApprovalDetail, string>({
                query: (id) => ({ url: `/api/approvals/${id}` }),
                providesTags: (result, error, id) => [{ type: 'approval_detail', id }]
            }),
            actOnApproval: build.mutation<{ success: boolean; state: string }, { id: string; body: ActionRequest }>({
                query: ({ id, body }) => ({ url: `/api/approvals/${id}/action`, method: 'POST', body }),
                invalidatesTags: (result, error, { id }) => [
                    { type: 'approval_detail', id },
                    'approvals_list',
                    'approvals_count'
                ]
            }),
            getEmailLanding: build.query<EmailLandingResponse, string>({
                query: (token) => ({ url: `/api/approvals/email/${token}` })
            }),
            getForwardGroups: build.query<GroupOption[], void>({
                query: () => ({ url: '/api/approvals/groups' })
            }),
            confirmEmail: build.mutation<{ success: boolean; state: string }, { token: string; body: ActionRequest }>({
                query: ({ token, body }) => ({ url: `/api/approvals/email/${token}/confirm`, method: 'POST', body }),
                invalidatesTags: ['approvals_list', 'approvals_count']
            })
        }),
        overrideExisting: false
    });

export default ApprovalsAPI;

export const {
    useGetPendingApprovalCountQuery,
    useGetPendingApprovalsQuery,
    useGetApprovalQuery,
    useActOnApprovalMutation,
    useGetEmailLandingQuery,
    useConfirmEmailMutation,
    useGetForwardGroupsQuery
} = ApprovalsAPI;
